'use client'

import { useState, useEffect, useMemo } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Calendar, Clock, Search, User, ArrowRight, Tag, Edit, Eye, Trash2, Settings } from "lucide-react"
import Link from "next/link"
import { useAuth } from "@/hooks/use-auth"
import { toast } from "sonner"
import { ROLES } from "@/lib/constants"

interface NewsArticle {
  id: string
  title: string
  slug: string
  excerpt?: string
  isPublished: boolean
  isFeatured: boolean
  publishedAt: string | null
  createdAt: string
  updatedAt: string
  viewCount: number
  tags: string[]
  author: {
    id: string
    username: string
  }
}

interface NewsPageClientProps {
  initialArticles: NewsArticle[]
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

const getTagColor = (tag: string) => {
  const colors: Record<string, string> = {
    infrastructure: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
    security: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    maintenance: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    announcement: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    upgrade: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400",
  }
  return colors[tag] || "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
}

export function NewsPageClient({ initialArticles }: NewsPageClientProps) {
  const [articles, setArticles] = useState<NewsArticle[]>(initialArticles)
  const [search, setSearch] = useState("")
  const [selectedTag, setSelectedTag] = useState("all")
  const [sortBy, setSortBy] = useState("newest")
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)
  
  const { user, hasRole } = useAuth()
  const isAdmin = user && (hasRole(ROLES.SITE_ADMIN) || hasRole(ROLES.NEWS_EDITOR))

  // Get unique tags from articles
  const availableTags = useMemo(() => {
    const allTags = articles.flatMap(article => article.tags)
    return Array.from(new Set(allTags)).sort()
  }, [articles])

  // Filter and sort articles
  const filteredArticles = useMemo(() => {
    let filtered = articles

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(article => 
        article.title.toLowerCase().includes(searchLower) ||
        (article.excerpt?.toLowerCase().includes(searchLower)) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchLower))
      )
    }

    // Tag filter
    if (selectedTag !== "all") {
      filtered = filtered.filter(article => 
        article.tags.includes(selectedTag)
      )
    }

    // Sort articles
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.publishedAt || a.createdAt)
      const dateB = new Date(b.publishedAt || b.createdAt)
      
      if (sortBy === "newest") {
        return dateB.getTime() - dateA.getTime()
      } else if (sortBy === "oldest") {
        return dateA.getTime() - dateB.getTime()
      }
      return 0
    })

    return filtered
  }, [articles, search, selectedTag, sortBy])

  const featuredArticle = filteredArticles.find((article) => article.isFeatured)
  const regularArticles = filteredArticles.filter((article) => !article.isFeatured)

  // Delete article
  const handleDeleteArticle = async (articleId: string) => {
    try {
      setDeleting(articleId)
      
      const response = await fetch(`/api/news/${articleId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete article')
      }

      // Remove article from local state
      setArticles(prev => prev.filter(article => article.id !== articleId))
      toast.success('Article deleted successfully')
    } catch (error) {
      console.error('Error deleting article:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete article')
    } finally {
      setDeleting(null)
    }
  }

  // Load more articles
  const loadMoreArticles = async () => {
    if (loading || !hasMore) return

    try {
      setLoading(true)
      const response = await fetch(`/api/news?page=${page + 1}&limit=20&published=true`)
      const data = await response.json()

      if (data.success && data.data.articles.length > 0) {
        setArticles(prev => {
          const existingIds = new Set(prev.map(a => a.id))
          const newArticles = data.data.articles.filter((a: NewsArticle) => !existingIds.has(a.id))
          return [...prev, ...newArticles]
        })
        setPage(prev => prev + 1)
        setHasMore(data.data.pagination.hasNext)
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error loading more articles:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Admin Portal Link */}
      {isAdmin && (
        <div className="flex justify-end mb-6">
          <Button variant="outline" asChild>
            <Link href="/admin/news">
              <Settings className="w-4 h-4 mr-2" />
              News Admin Portal
            </Link>
          </Button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-12">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search news articles..." 
            className="pl-10" 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={selectedTag} onValueChange={setSelectedTag}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Filter by tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {availableTags.map((tag) => (
              <SelectItem key={tag} value={tag}>
                {tag}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Featured Article */}
      {featuredArticle && (
        <Card className="mb-12 overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3 bg-gradient-to-br from-primary via-primary/80 to-primary/60 p-8 flex items-center justify-center">
              <div className="text-primary-foreground text-center">
                <Badge className="bg-primary-foreground/20 text-primary-foreground mb-4">Featured</Badge>
                <h3 className="text-2xl font-bold mb-2">Latest Update</h3>
                <p className="text-primary-foreground/90">Important network news</p>
              </div>
            </div>
            <div className="md:w-2/3 p-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {featuredArticle.tags.map((tag) => (
                  <Badge key={tag} className={getTagColor(tag)}>
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
              <h2 className="text-3xl font-bold mb-4">{featuredArticle.title}</h2>
              <p className="text-muted-foreground mb-6 text-lg">{featuredArticle.excerpt}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <User className="h-4 w-4" />
                    <span>{featuredArticle.author.username}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(featuredArticle.publishedAt || featuredArticle.createdAt)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {isAdmin && (
                    <>
                      <Button variant="outline" size="sm" asChild>
                        <Link href={`/admin/news/${featuredArticle.id}/edit`}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Link>
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Featured Article</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{featuredArticle.title}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteArticle(featuredArticle.id)}
                              disabled={deleting === featuredArticle.id}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {deleting === featuredArticle.id ? 'Deleting...' : 'Delete Article'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                  <Button asChild>
                    <Link href={`/news/${featuredArticle.slug}`}>
                      <Eye className="mr-2 h-4 w-4" />
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Regular Articles Grid */}
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {regularArticles.map((article) => (
          <Card key={article.id} className="hover:shadow-lg transition-shadow group">
            <CardHeader>
              <div className="flex flex-wrap gap-1 mb-3">
                {article.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
              <CardTitle className="group-hover:text-primary transition-colors">
                <Link href={`/news/${article.slug}`}>{article.title}</Link>
              </CardTitle>
              <CardDescription className="line-clamp-3">{article.excerpt}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                <div className="flex items-center space-x-1">
                  <User className="h-3 w-3" />
                  <span>{article.author.username}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>{formatDate(article.publishedAt || article.createdAt)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {isAdmin ? (
                  <>
                    <Button variant="ghost" size="sm" className="flex-1" asChild>
                      <Link href={`/admin/news/${article.id}/edit`}>
                        <Edit className="w-3 h-3 mr-2" />
                        Edit
                      </Link>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="flex-1 text-destructive hover:bg-destructive hover:text-destructive-foreground">
                          <Trash2 className="w-3 h-3 mr-2" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Article</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{article.title}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteArticle(article.id)}
                            disabled={deleting === article.id}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {deleting === article.id ? 'Deleting...' : 'Delete Article'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button variant="ghost" className="flex-1 group-hover:bg-muted" asChild>
                      <Link href={`/news/${article.slug}`}>
                        Read Article
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" className="w-full group-hover:bg-muted" asChild>
                    <Link href={`/news/${article.slug}`}>
                      Read Article
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* No results message */}
      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium mb-2">No articles found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search or filter criteria.
          </p>
          <Button variant="outline" onClick={() => {
            setSearch("")
            setSelectedTag("all")
          }}>
            Clear Filters
          </Button>
        </div>
      )}

      {/* Load More */}
      {hasMore && filteredArticles.length > 0 && (
        <div className="text-center mt-12">
          <Button 
            variant="outline" 
            size="lg"
            onClick={loadMoreArticles}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load More Articles"}
          </Button>
        </div>
      )}

      {/* Loading More Articles Skeleton */}
      {loading && (
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mt-12">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-muted rounded-lg animate-pulse h-80"></div>
          ))}
        </div>
      )}

      {/* Newsletter Signup */}
      <Card className="mt-16 bg-gradient-to-r from-sky-50 via-cyan-50 to-emerald-50 dark:from-sky-950/20 dark:via-cyan-950/20 dark:to-emerald-950/20 border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Stay Updated</CardTitle>
          <CardDescription className="text-lg">
            Subscribe to our newsletter to receive important network updates and announcements.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input placeholder="Enter your email" type="email" className="flex-1" />
            <Button>Subscribe</Button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </CardContent>
      </Card>
    </>
  )
}